<template>
  <b-modal
    v-model="showModal"
    title="Pack , Pick & Deliver Bill"
    :size="'sm'"
    centered
    no-close-on-backdrop
  >
    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div
        v-if="typeof loadingError === 'object'"
        class="alert-body"
      >
        <p
          v-for="(message, index) in loadingError"
          :key="index"
        >
          {{ message }}
        </p>
      </div>
      <p
        v-else
        class="alert-body"
      >
        {{ loadingError }}
      </p>
    </b-alert>

    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>
    <b-form @submit.prevent="submitHandler">
      <b-form-group label="User">
        <custom-v-select
          ref="user"
          v-model="selectedUserId"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :options="filteredUsers"
          label="name"
          placeholder="Type to search user..."
          :reduce="(item) => item.id"
          :filterable="true"
          @search="handleUserSearch"
        />
      </b-form-group>
    </b-form>
    <b-alert
      variant="danger"
      :show="submitionError !== null ? true : false"
    >
      <div
        v-if="typeof submitionError === 'string'"
        class="alert-body"
      >
        <p>
          {{ submitionError }}
        </p>
      </div>
      <template v-if="submitionError && submitionError.detail">
        <div
          v-if="typeof submitionError.detail === 'object'"
          class="alert-body"
        >
          <p
            v-for="(message, index) in submitionError.detail"
            :key="index"
          >
            {{ message }}
          </p>
        </div>
        <p
          v-else
          class="alert-body"
        >
          {{ submitionError.detail }}
        </p>
      </template>
    </b-alert>
    <div v-if="!loadingError">
      <p>
        Are you sure you want to proceed with packing, picking, and delivery ?</p>
    </div>
    <template #modal-footer>
      <div class="w-100 d-flex justify-content-end">
        <b-button
          class="mr-1"
          variant="secondary"
          @click="showModal=false"
        >
          Close
        </b-button>
        <b-button
          variant="primary"
          :disabled="loading || !!loadingError || submitting"
          @click="handleConfirm"
        >
          Confirm
          <b-spinner
            v-if="submitting"
            small
            label="Small Spinner"
          />
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BSpinner, BAlert, BButton, BForm, BFormGroup,
} from 'bootstrap-vue'
import axios from 'axios'
import CustomVSelect from '@/components/UI/CustomVSelect.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BModal,
    BSpinner,
    BAlert,
    BButton,
    BForm,
    BFormGroup,
    CustomVSelect,
  },
  props: {
    value: { type: Boolean, default: false },
    operationUsers: {
      type: Array,
      required: true,
    },
    billDeliveryIds: { type: Array, required: true },
    billRgStatusIds: { type: Array, required: true },
  },
  data() {
    return {
      loading: false,
      submitting: false,
      loadingError: null,
      selectedUserId: null,
      submitionError: null,
      filteredUsers: [],
    }
  },
  computed: {
    showModal: {
      get() {
        return this.value
      },
      set(newVal) {
        this.$emit('input', newVal)
      },
    },
  },

  mounted() {
    this.fetchOptions()
  },

  methods: {
    handleUserSearch(search) {
      this.searchQuery = search
      if (!search || search.trim() === '') {
        // Keep selected user visible even when search is empty
        if (this.selectedUserId) {
          const selectedUser = this.operationUsers.find(user => user.id === this.selectedUserId)
          this.filteredUsers = selectedUser ? [selectedUser] : []
        } else {
          this.filteredUsers = []
        }
      } else {
        this.filteredUsers = this.operationUsers
      }
    },
    async fetchOptions() {
      this.loading = true
      this.loadingError = null

      try {
        await axios.post('/delivery/update_bills_status', {
          bills_delivery_id: this.billDeliveryIds,
          action: 'PICK+DELIVER',
          dry_run: true,
        })
      } catch (error) {
        this.loadingError = error?.response?.data?.detail || 'Failed to check bill status!'
        this.loading = false
        return
      }

      this.loading = false
    },

    async handleConfirm() {
      // Separate bills by status
      const createdBills = this.billRgStatusIds.filter(bill => bill.status === 'Created')
      const packingInitiatedBills = this.billRgStatusIds.filter(bill => bill.status === 'Packing Initiated')

      // Check if user is required and selected
      if (createdBills.length > 0 && !this.selectedUserId) {
        this.submitionError = 'Please select a user for bills with "Created" status'
        return
      }

      this.submitionError = null
      this.submitting = true

      try {
        // Collect all bill IDs that will need packing completed
        let billsForPackingCompleted = [...packingInitiatedBills.map(bill => bill.id)]

        // First API call - Initiate packing for Created bills
        if (createdBills.length > 0) {
          const initiateRes = await axios.post('/operations/initiate-packing', {
            bill_rg_status_ids: createdBills.map(bill => bill.id),
            operation_user_id: this.selectedUserId,
          })

          this.$toast({
            component: ToastificationContent,
            props: {
              title: initiateRes.data.message,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })

          // Add these IDs to the packing completed list
          billsForPackingCompleted = [...billsForPackingCompleted, ...createdBills.map(bill => bill.id)]
        }

        // Second API call - Mark packing completed for all bills
        if (billsForPackingCompleted.length > 0) {
          const packingRes = await axios.post('/operations/mark-packing-completed', {
            bill_rg_status_ids: billsForPackingCompleted,
          })

          this.$toast({
            component: ToastificationContent,
            props: {
              title: packingRes.data.message,
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        }

        // Third API call - Update bills delivery status (PICK+DELIVER)
        const formData = new FormData()

        for (let x = 0; x < this.billDeliveryIds.length; x += 1) {
          formData.append('bills_delivery_id', this.billDeliveryIds[x])
        }
        formData.append('action', 'PICK+DELIVER')
        formData.append('delivery_mode', 'SELF_COLLECTION')

        const deliveryRes = await axios.post('/delivery/update_bills_status', formData)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: deliveryRes.data.detail,
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        this.submitting = false
        this.$emit('updated')
        this.showModal = false
      } catch (error) {
        const statusCode = error?.response?.status
        const serverErrors = error?.response?.data

        if (statusCode === 400 && serverErrors) {
          this.submitionError = serverErrors?.detail
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Please correct the form errors',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        } else {
          this.submitionError = serverErrors?.detail || 'Failed to update the status!'
        }

        this.submitting = false
      }
    },
  },
}
</script>
